// What the four funnel numbers MEAN, in one place.
//
// They are counts of three different units — pages, overlays, clicks — and read
// as a descending funnel they do not descend. Measured on the live account,
// 30 days: 6,648 pageviews, 37,666 overlays delivered, 22,559 viewed, 39 clicks.
// Delivered is 5.7x pageviews, because a page carries many images. Correct
// arithmetic, unreadable without the unit.
//
// Analytics carried these sub-lines and the Dashboard rendered the same four
// labels bare, so a glance at the Dashboard showed a funnel that rises 5.7x at
// step two and then collapses to 39. Extracted rather than copied: a rule stated
// twice eventually gets stated differently, and these two screens sum the SAME
// daily series to produce the SAME four numbers.
//
// Pure arithmetic and strings, no component: testable without a router, the same
// reason `ctr-reachability` lives on its own.
import type { AnalyticsDailyPoint } from '../services/apiService';

export interface FunnelUnitsInput {
  daily: AnalyticsDailyPoint[];
  /** ISO timestamp view tracking began, or null. */
  viewTrackingStartedAt: string | null;
  /** e.g. "Last 30 days" — appears verbatim in the copy. */
  periodLabel: string;
}

export interface FunnelUnits {
  pageviews: string;
  delivered: string;
  viewed: string;
  clicks: string;
}

/**
 * The sub-line under each funnel number: what unit it counts, and how it
 * reconciles with the number above it.
 *
 * View tracking began part-way through the product's life, so any ratio whose
 * denominator is a SERVE and whose numerator is a VIEW must pair only days both
 * were recorded on — otherwise a window reaching back before the cutoff divides
 * whole-period serves by part-period views and understates the share. Days at or
 * before the cutoff day are excluded from that pairing, never from the raw
 * counts, which stay whole-window.
 */
export function funnelUnits({ daily, viewTrackingStartedAt, periodLabel }: FunnelUnitsInput): FunnelUnits {
  const impressions = daily.reduce((s, d) => s + (d.impressions ?? 0), 0);
  const serves = daily.reduce((s, d) => s + d.matchesServed, 0);
  const views = daily.reduce((s, d) => s + d.views, 0);

  const cutoffDay = viewTrackingStartedAt ? viewTrackingStartedAt.slice(0, 10) : null;
  const after = (d: AnalyticsDailyPoint) => cutoffDay != null && d.day > cutoffDay;
  const postCutoffServes = cutoffDay ? daily.filter(after).reduce((s, d) => s + d.matchesServed, 0) : 0;
  const postCutoffClicks = cutoffDay ? daily.filter(after).reduce((s, d) => s + d.clicks, 0) : 0;
  const ctrViews = cutoffDay ? daily.filter(after).reduce((s, d) => s + d.views, 0) : 0;

  // "recent" when the window reaches back past full view tracking, so the reader
  // knows the ratio describes part of the window rather than all of it.
  const windowsMisaligned = cutoffDay != null && daily.length > 0 && cutoffDay >= daily[0].day;

  const coverage = impressions > 0 ? serves / impressions : null;
  const viewedShare = views > 0 && postCutoffServes > 0 ? (views / postCutoffServes) * 100 : null;
  const viewsPerClick = postCutoffClicks > 0 && ctrViews > 0 ? Math.round(ctrViews / postCutoffClicks) : null;

  return {
    pageviews: `pages where your widget ran (${periodLabel})`,
    delivered: coverage != null
      ? `~${coverage.toFixed(1)} per pageview (${periodLabel}, approx.)`
      : `approximate (${periodLabel})`,
    viewed: viewedShare != null
      ? `${viewedShare.toFixed(1)}% of ${windowsMisaligned ? 'recent ' : ''}delivered`
      : 'at least 50% visible for 1s — approximate',
    clicks: viewsPerClick != null
      ? (windowsMisaligned
          ? `~1 recent click per ${viewsPerClick} overlay views`
          : `~1 per ${viewsPerClick} overlay views`)
      : `via our redirect (${periodLabel})`,
  };
}
